import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ClipboardCheck, KeyRound, ShieldCheck, UserPlus } from "lucide-react";
import { ApiError, loadCsrfToken } from "../api/client";
import { register, registerOptions } from "../api/auth";
import { useAuth } from "../hooks/useAuth";
import {
  buildRegisterPayload,
  emptyRegisterForm,
  registerProgramOptions,
  registerRoleOptions,
  registerUnitOptions,
  scopeRequirements,
  type RegisterFormState,
} from "../lib/user";
import { Alert, Button, Card, Field, Input, Select } from "../components/ui";

/**
 * Pendaftaran mandiri (rute publik).
 * Akun yang dibuat di sini berstatus PENDING dan baru dapat dipakai setelah
 * disetujui admin program studi, sehingga halaman ini tidak membuat sesi.
 */
export function RegisterPage() {
  const { isAuthed } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState<RegisterFormState>(() => emptyRegisterForm());
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState<{ username: string; message: string } | null>(null);

  // Formulir publik butuh token CSRF anonim sebelum bisa dikirim.
  useEffect(() => {
    void loadCsrfToken();
  }, []);

  useEffect(() => {
    if (isAuthed) navigate("/", { replace: true });
  }, [isAuthed, navigate]);

  const optionsQuery = useQuery({
    queryKey: ["register", "options"],
    queryFn: registerOptions,
    staleTime: 5 * 60 * 1000,
  });

  const roleOptions = useMemo(
    () => registerRoleOptions(optionsQuery.data?.roles),
    [optionsQuery.data],
  );
  const programOptions = useMemo(
    () => registerProgramOptions(optionsQuery.data),
    [optionsQuery.data],
  );
  const unitOptions = useMemo(
    () => registerUnitOptions(optionsQuery.data, form.studyProgramId),
    [optionsQuery.data, form.studyProgramId],
  );

  const requirements = scopeRequirements(form.role);

  function update<K extends keyof RegisterFormState>(key: K, value: RegisterFormState[K]): void {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (form.password !== confirmPassword) {
      setError("Ulangi kata sandi tidak sama.");
      return;
    }

    let payload;
    try {
      payload = buildRegisterPayload(form);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Data pendaftaran belum lengkap.");
      return;
    }

    setBusy(true);
    try {
      const result = await register(payload);
      setSubmitted({ username: result.user.username, message: result.message });
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Pendaftaran gagal dikirim. Periksa koneksi lalu coba lagi.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh bg-slate-100 px-4 py-10">
      <div className="mx-auto w-full max-w-5xl space-y-6">
        <header className="space-y-1 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 ring-1 ring-brand-200">
            <ShieldCheck className="size-4" aria-hidden />
            ISTN
          </span>
          <h1 className="text-2xl font-bold text-slate-900">Pendaftaran Akun</h1>
          <p className="text-sm text-slate-600">
            ISTN Activity Hub — pencatatan, kurasi, dan validasi kegiatan seluruh fakultas dan
            program studi di Institut Sains dan Teknologi Nasional.
          </p>
        </header>

        {submitted ? (
          <Card className="mx-auto max-w-2xl space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 size-6 text-emerald-600" aria-hidden />
              <div className="space-y-1">
                <h2 className="font-semibold text-slate-900">Pendaftaran terkirim</h2>
                <p className="text-sm text-slate-700">{submitted.message}</p>
                <p className="text-sm text-slate-500">
                  Username Anda:{" "}
                  <span className="font-medium text-slate-700">{submitted.username}</span>
                </p>
              </div>
            </div>
            <Alert tone="info">
              Setelah disetujui, Anda dapat masuk memakai username (atau email/nomor induk) beserta
              kata sandi yang baru saja dibuat. Bila ditolak, hubungi admin program studi Anda.
            </Alert>
            <div className="flex flex-wrap gap-2">
              <Link to="/login">
                <Button>Ke halaman masuk</Button>
              </Link>
              <Button
                variant="secondary"
                onClick={() => {
                  setSubmitted(null);
                  setForm(emptyRegisterForm());
                  setConfirmPassword("");
                }}
              >
                Daftarkan akun lain
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
            <Card className="space-y-4">
              <h2 className="flex items-center gap-2 font-semibold text-slate-800">
                <UserPlus className="size-4" aria-hidden />
                Data pendaftar
              </h2>

              {optionsQuery.isError ? (
                <Alert tone="error">
                  Daftar peran dan program studi gagal dimuat. Muat ulang halaman ini.
                </Alert>
              ) : null}

              <form
                className="grid gap-3 sm:grid-cols-2"
                onSubmit={(event) => void onSubmit(event)}
              >
                <Field
                  label="Username"
                  htmlFor="reg-username"
                  hint="Huruf, angka, titik, garis bawah."
                >
                  <Input
                    id="reg-username"
                    autoComplete="username"
                    required
                    minLength={3}
                    maxLength={60}
                    placeholder="mis. anisa.putri"
                    value={form.username}
                    onChange={(event) => update("username", event.target.value)}
                  />
                </Field>

                <Field label="Nama lengkap" htmlFor="reg-full-name">
                  <Input
                    id="reg-full-name"
                    autoComplete="name"
                    required
                    minLength={3}
                    maxLength={150}
                    placeholder="mis. Anisa Putri"
                    value={form.fullName}
                    onChange={(event) => update("fullName", event.target.value)}
                  />
                </Field>

                <Field label="Email" htmlFor="reg-email">
                  <Input
                    id="reg-email"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={150}
                    placeholder="mis. anisa@istn.ac.id"
                    value={form.email}
                    onChange={(event) => update("email", event.target.value)}
                  />
                </Field>

                <Field
                  label="Nomor induk"
                  htmlFor="reg-identifier"
                  hint="NIM/NIDN/NIP. Opsional, tetapi tidak boleh sama dengan akun lain."
                >
                  <Input
                    id="reg-identifier"
                    maxLength={60}
                    placeholder="mis. NIM-2026-FA-099"
                    value={form.identifierNumber}
                    onChange={(event) => update("identifierNumber", event.target.value)}
                  />
                </Field>

                <Field label="Peran" htmlFor="reg-role">
                  <Select
                    id="reg-role"
                    value={form.role}
                    onChange={(event) => {
                      update("role", event.target.value as RegisterFormState["role"]);
                      update("unitId", "");
                    }}
                  >
                    {roleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                {requirements.needsStudyProgram ? (
                  <Field label="Program studi" htmlFor="reg-study-program">
                    <Select
                      id="reg-study-program"
                      required
                      value={form.studyProgramId}
                      onChange={(event) => {
                        update("studyProgramId", event.target.value);
                        update("unitId", "");
                      }}
                    >
                      <option value="">Pilih program studi</option>
                      {programOptions.map((program) => (
                        <option key={program.id} value={program.id}>
                          {program.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : null}

                {requirements.needsUnit ? (
                  <Field
                    label="Unit"
                    htmlFor="reg-unit"
                    hint="Unit harus berada di program studi yang dipilih."
                  >
                    <Select
                      id="reg-unit"
                      required
                      value={form.unitId}
                      onChange={(event) => update("unitId", event.target.value)}
                    >
                      <option value="">Pilih unit</option>
                      {unitOptions.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : null}

                <Field
                  label="Kata sandi"
                  htmlFor="reg-password"
                  hint="Minimal 10 karakter serta memuat huruf besar, huruf kecil, dan angka."
                >
                  <Input
                    id="reg-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    maxLength={200}
                    value={form.password}
                    onChange={(event) => update("password", event.target.value)}
                  />
                </Field>

                <Field label="Ulangi kata sandi" htmlFor="reg-password-confirm">
                  <Input
                    id="reg-password-confirm"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    maxLength={200}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                </Field>

                <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2">
                  <input
                    type="checkbox"
                    className="size-4 rounded border-slate-300"
                    checked={showPassword}
                    onChange={(event) => setShowPassword(event.target.checked)}
                  />
                  Tampilkan kata sandi
                </label>

                {error ? (
                  <div className="sm:col-span-2">
                    <Alert tone="error">{error}</Alert>
                  </div>
                ) : null}

                <div className="sm:col-span-2">
                  <Button type="submit" loading={busy}>
                    <KeyRound className="size-4" aria-hidden />
                    Kirim pendaftaran
                  </Button>
                </div>
              </form>
            </Card>

            <Card className="space-y-3">
              <h2 className="flex items-center gap-2 font-semibold text-slate-800">
                <ClipboardCheck className="size-4" aria-hidden />
                Alur pendaftaran
              </h2>
              <ol className="space-y-2 text-sm text-slate-600">
                <li>1. Isi data di samping, lalu kirim pendaftaran.</li>
                <li>
                  2. Akun berstatus{" "}
                  <span className="font-medium text-slate-800">menunggu persetujuan</span> admin
                  program studi.
                </li>
                <li>3. Admin menyetujui atau menolak (beserta alasannya).</li>
                <li>
                  4. Setelah disetujui, Anda dapat masuk memakai username, email, atau nomor induk.
                </li>
              </ol>
              <Alert tone="info">
                Peran admin (admin program studi, fakultas, institut) tidak dapat didaftarkan
                sendiri — hubungi administrator institut.
              </Alert>
              <p className="text-sm text-slate-600">
                Sudah punya akun?{" "}
                <Link to="/login" className="font-semibold text-brand-700 underline">
                  Masuk di sini
                </Link>
                .
              </p>
            </Card>
          </div>
        )}

        <footer className="pb-2 text-center text-xs text-slate-500">
          Institut Sains dan Teknologi Nasional
        </footer>
      </div>
    </div>
  );
}
